import { useMemo } from 'react';
import { type ResidualValueSettingsPayload } from '../../../../../../api/types';
import { type NumberFormatFn } from '../../../../../../utilities/useFormats';
import { type VariantOption } from '../../../shared/useSelectedVariants';
import { type LeasePurchaseTableRecord } from './shared';

const useRecords = (
    variants: VariantOption[],
    residualValueSetting: ResidualValueSettingsPayload,
    terms: number[],
    averageMileages: number[],
    formatAmount: NumberFormatFn,
    mileageUnitLabel: string
): LeasePurchaseTableRecord[] =>
    useMemo<LeasePurchaseTableRecord[]>(() => {
        if (!residualValueSetting || !terms?.length || !variants?.length) {
            return [];
        }

        const { table } = residualValueSetting;

        const getValue = (variantSuiteId: string, term: number, mileage: number) => {
            const row = table.find(
                row => row.variantSuiteId === variantSuiteId && row.term === term && row.mileage === mileage
            );

            return row?.value;
        };

        const results: LeasePurchaseTableRecord[] = [];

        variants.forEach(variant => {
            const variantMileage: LeasePurchaseTableRecord[] = averageMileages.map(mileage => {
                const termsValue = {};

                terms.forEach(term => {
                    termsValue[`term${term}`] = getValue(variant.id, term, mileage);
                });

                return {
                    key: variant.id,
                    variantSuiteId: variant.id,
                    variantId: variant.identifier,
                    mileage,
                    variantName: variant.name,
                    variantNameMileage: `${variant.name} (${variant.identifier}) | ${formatAmount(
                        mileage
                    )} ${mileageUnitLabel}`,
                    ...termsValue,
                };
            });

            results.push(...variantMileage);
        });

        return results;
    }, [residualValueSetting, terms, variants, averageMileages, formatAmount, mileageUnitLabel]);

export default useRecords;
